import React, { useEffect, useState } from 'react';
import { api } from '../../services/authService';
import { conditionDisplayLabel, columnOptionLabels } from './criteriaUtils';

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

const columnsCache = new Map();
const columnsPromises = new Map();

function folderPathKey(folderPath) {
  if (Array.isArray(folderPath)) {
    return folderPath.filter(Boolean).join(',');
  }
  return String(folderPath || '').trim();
}

function loadColumnOptions(folderPath) {
  const key = folderPathKey(folderPath);
  if (columnsCache.has(key)) return Promise.resolve(columnsCache.get(key));
  if (!columnsPromises.has(key)) {
    const params = { size: 500, visible: true };
    if (key) params.folder_path = key;
    const promise = api
      .get('/api/v2/segments/column-options', { params })
      .then((res) => {
        const data = {
          hits: Array.isArray(res.data?.hits) ? res.data.hits : [],
          error: res.data?.error || '',
        };
        columnsCache.set(key, data);
        return data;
      })
      .catch((e) => {
        columnsPromises.delete(key);
        throw e;
      });
    columnsPromises.set(key, promise);
  }
  return columnsPromises.get(key);
}

function hitMatchesCondition(hit, condition) {
  return (
    String(hit.catalog || '') === String(condition.catalog || '') &&
    String(hit.schema || '') === String(condition.schema || '') &&
    String(hit.table || '') === String(condition.table || '') &&
    String(hit.column || '') === String(condition.column || '')
  );
}

const ColumnTypeahead = ({ condition, onSelect, folderPath = [] }) => {
  const pathKey = folderPathKey(folderPath);
  const cached = columnsCache.get(pathKey);
  const [options, setOptions] = useState(cached?.hits || []);
  const [loading, setLoading] = useState(!cached);
  const [loadError, setLoadError] = useState(cached?.error || '');

  useEffect(() => {
    let cancelled = false;
    const existing = columnsCache.get(pathKey);
    setLoading(!existing);
    setLoadError(existing?.error || '');
    if (existing) setOptions(existing.hits);

    loadColumnOptions(pathKey)
      .then((data) => {
        if (!cancelled) {
          setOptions(data.hits);
          setLoadError(data.error || '');
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setOptions([]);
          setLoadError(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load columns');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pathKey]);

  const selectedId = options.find((h) => hitMatchesCondition(h, condition))?._id || '';
  const pickedLabel = conditionDisplayLabel(condition);
  const hasSelectionNotInList = Boolean(pickedLabel) && !selectedId;
  const selectValue = selectedId || (hasSelectionNotInList ? '__current__' : '');
  const optionLabels = columnOptionLabels(options);

  const handleChange = (docId) => {
    if (!docId || docId === '__current__') return;
    const hit = options.find((h) => h._id === docId);
    if (!hit) return;
    onSelect({
      catalog: hit.catalog || '',
      schema: hit.schema || '',
      table: hit.table || '',
      column: hit.column || '',
      display_name: hit.display_name || hit.column_name || hit.column || '',
      column_name: hit.display_name || hit.column_name || hit.column || '',
      column_type: hit.column_type || '',
      has_value_list: Boolean(hit.has_value_list),
    });
  };

  return (
    <div className="criteria-ta-wrap">
      <select
        className="criteria-col-input"
        value={selectValue}
        disabled={loading}
        aria-label="Select column"
        onChange={(e) => handleChange(e.target.value)}
      >
        <option value="" disabled={Boolean(pickedLabel)}>
          {loading ? 'Loading columns…' : 'Select column…'}
        </option>
        {hasSelectionNotInList && (
          <option value="__current__">{pickedLabel}</option>
        )}
        {options.map((h, index) => (
          <option key={h._id} value={h._id}>
            {optionLabels[index]}
          </option>
        ))}
      </select>
      {loadError && (
        <p className="criteria-empty" role="alert">
          {loadError}
        </p>
      )}
    </div>
  );
};

export default ColumnTypeahead;
